import { BranchesOutlined } from '@ant-design/icons';
import { Drawer } from '@framework/ui';
import { Button, ButtonProps, Checkbox, Space } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { IReference } from '../../../schema/expressions/references/IReference';
import { ConditionGroupExpressionSchema } from '../../../schema/expressions/repository/condition-group/ConditionGroupExpressionSchema';
import { ConditionGroupInput } from '../../../schema/expressions/repository/condition-group/input/ConditionGroupInput';
import { useTreeReferencesContext } from '../../../tree/references/TreeReferencesContext';
import { DrawerButtonRow } from '../../../ui/drawer';
import { modalConfirm } from '../../../ui/modal/modalConfirm';
import { IBooleanCondition } from './IBooleanCondition';

export interface IBooleanConditionEditorInputProps {
  name: string;
  label: string;
  hideStatic?: boolean;
  size?: ButtonProps['size'];
  resetToFalse?: boolean;
  /**
   * Permette di forzare la lista delle reference. Se non fornite, recupera
   * la lista dal context (TreeReferencesContext). Se fornite le usa al posto
   * di quelle del context.
   *
   * e.g. Utilizzate per la condizione di visibilità delle sezioni, che non può
   * appoggiarsi al context per il calcolo delle reference avendo una logica diversa.
   * (recupero tutte le reference delle altre sezioni).
   */
  forceReferences?: IReference[];
}

export function BooleanConditionEditorInput(
  props: IBooleanConditionEditorInputProps,
) {
  const { name, label, size, hideStatic, resetToFalse, forceReferences } =
    props;
  const { references } = useTreeReferencesContext();

  const [isOpen, setIsOpen] = useState(false);

  const methods = useFormContext();
  const { field } = useController({
    name,
    defaultValue: { type: 'static', value: false },
  });

  const value = useWatch({ name }) as IBooleanCondition;
  const staticValue = value?.type === 'static' && value.value;
  const isDynamic = value != null && value.type === 'expression';

  return (
    <div>
      {!hideStatic && (
        <Checkbox
          checked={staticValue}
          indeterminate={isDynamic}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onChange={async e => {
            if (isDynamic) {
              const confirmed = await modalConfirm({
                title: 'Rimuovere le regole?',
                content: `Le regole attualmente impostate verranno rimosse e verrà impostato un valore statico. Sei sicuro di voler continuare?`,
                okText: 'Rimuovi regole',
                okButtonProps: {
                  danger: true,
                },
                cancelText: 'Annulla',
              });
              if (!confirmed) return;
            }

            field.onChange({ type: 'static', value: e.target.checked });
            field.onBlur();
          }}
        >
          {label}
        </Checkbox>
      )}
      {hideStatic && <span>{label} </span>}
      <Button
        size={size ?? 'small'}
        type={isDynamic ? 'primary' : 'default'}
        icon={<BranchesOutlined />}
        onClick={() => {
          if (value == null || value.type !== 'expression') {
            field.onChange({
              type: 'expression',
              expression: ConditionGroupExpressionSchema.cast(undefined),
            });
            field.onBlur();
          }
          setIsOpen(true);
        }}
      >
        Regole
      </Button>
      <Drawer
        title={`Regole: ${label}`}
        size="large"
        placement="right"
        open={isOpen}
        onClose={() => setIsOpen(false)}
      >
        {isDynamic && (
          <ConditionGroupInput
            name={`${name}.expression`}
            references={forceReferences ?? references}
          />
        )}
        <DrawerButtonRow>
          <Space>
            <Button
              size="large"
              danger
              onClick={() => {
                setIsOpen(false);
                field.onChange(
                  resetToFalse
                    ? { type: 'static', value: false }
                    : { type: 'static', value: true },
                );
                field.onBlur();
              }}
            >
              Rimuovi
            </Button>
            <Button
              size="large"
              type="primary"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={async () => {
                // Permette di validare il form prima di chiudere il drawer,
                // in modo da mostrare gli errori.
                const isValid = await methods.trigger(name, {
                  shouldFocus: true,
                });

                if (isValid) {
                  setIsOpen(false);
                }
              }}
            >
              Applica
            </Button>
          </Space>
        </DrawerButtonRow>
      </Drawer>
    </div>
  );
}
